import { portalText, usePortalTranslation, portalLocale } from "../../i18n/portal.js";
/**
 * `data-screen-label="Channel"` for web chat — the Web chat page (plan-004 WC-81, FR-WC-15..17,
 * FR-WC-19, FR-WC-20; review #175–#180; design §17.10.5, IC-37).
 *
 * Laid out as the design's Channel detail (WeTalk.dc.html line 2646, ADR 0018): the back link, the
 * mark, the name with its state and the blurb, the three figures — then, where the design's web
 * chat had only "Copy the snippet / Choose where it appears / Pick the agent", one card per website
 * widget (every voice agent's, `widget-card.tsx`) and the Connect card.
 *
 * THE PAGE NEVER HANGS (#175). The read resolves to the page, or to an error that says what failed,
 * carries the request id to quote, and offers Retry — and a read that has not answered after
 * `slowNoticeMs` says so and offers Retry too, instead of "Opening…" forever.
 *
 * ROLES. Any member reads the page; only Owners and Admins change the website chat (the API's
 * `agent:write` for owner or admin). Members and Viewers see every widget, its sites and its
 * snippet, with the controls withheld and the reason in plain words.
 *
 * WHILE WEB CHAT IS WAITLISTED (the catalogue says so until the widget is deployed, WC-99), the page
 * says so with the API's reason and offers no Connect that the API would refuse, but still lists any
 * widget that exists, so an owner can see and disconnect it.
 */
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ApiError } from "@wetalk/api-client";
import { Button, Icon, Tag } from "@wetalk/ui";
import { useEffect, useState, type ReactNode } from "react";

import "./messaging.css";

import { MEDIUM_GLYPH, MEDIUM_STATE_LABEL, MEDIUM_STATE_TONE } from "./messaging-screen.js";
import { ConnectWidgetPanel } from "./connect-widget-panel.js";
import { WidgetCard } from "./widget-card.js";
import { GLYPH } from "../../shell/glyph.js";
import { useMemberCan } from "../../shell/member-capability.js";
import { apiErrorText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import { QUERY_KEY } from "../../api/query.js";
import type { ConsoleGateway } from "../../api/gateway.js";
import { MessagingReadError, type WebChatDetailView } from "../../api/slice/messaging.js";
import { talkTimeText } from "../../talk-time.js";

/** After this long without an answer the page says the read is slow and offers Retry (#175). */
export const WEB_CHAT_SLOW_NOTICE_MS = 8_000;

export const webChatDetailQuery = (gateway: ConsoleGateway) =>
  queryOptions({
    // Under the Messaging prefix, so every Messaging write refreshes it too.
    queryKey: [...QUERY_KEY.messagingMedium, "web_chat", "widget"],
    queryFn: () => gateway.getWebChatDetail(),
  });

/** The sentence and the request id for a failed read or write. */
export function readProblemOf(
  error: unknown,
  otherwise: string,
): { readonly text: string; readonly requestId: string | null } {
  if (error instanceof ApiError) return { text: portalLocale() === "en" ? error.message : apiErrorText(error, otherwise), requestId: error.requestId };
  if (error instanceof MessagingReadError) return { text: apiErrorText(error, ""), requestId: error.requestId };
  return { text: apiErrorText(error, otherwise), requestId: null };
}

export interface WebChatDetailProps {
  /** `?agent=` from the Channels tab: pre-selected in Connect when that agent is live. */
  readonly requestedVoiceAgentId: string | null;
  readonly slowNoticeMs: number;
}

export function WebChatDetail({ requestedVoiceAgentId, slowNoticeMs }: WebChatDetailProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const query = webChatDetailQuery(gateway);
  const detail = useQuery(query);

  if (detail.isError) {
    const problem = readProblemOf(detail.error, portalText("operations", "the_web_chat_page_could_not_be_read_f382449f"));
    return (
      <section className="c-page mg-detail-page">
        <BackLink />
        <div className="mg-problem" role="alert">
          <p className="mg-problem-title">{portalText("operations", "the_web_chat_page_could_not_be_opened_706661be")}</p>
          <p className="mg-card-note">
            {problem.text} {portalText("operations", "nothing_has_changed_9f5f5b8a")}{problem.requestId === null ? null : (
              <>
                {" "}
                {portalText("operations", "request_id_cbe1c0a3")}<span className="mg-request-id">{problem.requestId}</span>.
              </>
            )}
          </p>
          <div className="mg-card-actions">
            <Button
              variant="primary"
              disabled={detail.isFetching}
              onClick={() => {
                void detail.refetch();
              }}
            >
              {detail.isFetching ? portalText("operations", "trying_again_4900bdd3") : portalText("operations", "retry_9f5cd8a2")}
            </Button>
          </div>
        </div>
      </section>
    );
  }

  if (detail.data === undefined) {
    return (
      <SlowRead
        opening="Opening the web chat…"
        slowNoticeMs={slowNoticeMs}
        onRetry={() => {
          // A read with no answer yet is deduplicated by a plain refetch, so it is cancelled first.
          void queryClient.cancelQueries({ queryKey: query.queryKey }).then(() => detail.refetch());
        }}
      />
    );
  }

  return <WebChatPage detail={detail.data} requestedVoiceAgentId={requestedVoiceAgentId} />;
}

function BackLink(): ReactNode {
  usePortalTranslation("operations");
  return (
    <Link to="/messaging" className="mg-backlink">
      <Icon glyph={GLYPH.arrowLeft} size="sm" />
      {portalText("operations", "all_channels_844df142")}</Link>
  );
}

/** "Opening…", then — past `slowNoticeMs` — that it is slow, with Retry (review #175). */
export function SlowRead({
  opening,
  slowNoticeMs,
  onRetry,
}: {
  readonly opening: string;
  readonly slowNoticeMs: number;
  readonly onRetry: () => void;
}): ReactNode {
  usePortalTranslation("operations");
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const handle = globalThis.setTimeout(() => {
      setSlow(true);
    }, slowNoticeMs);
    return () => {
      globalThis.clearTimeout(handle);
    };
  }, [slowNoticeMs]);

  return (
    <section className="c-page mg-detail-page">
      <BackLink />
      <p className="c-note" role="status">
        {opening}
      </p>
      {slow ? (
        <div className="mg-problem" role="alert">
          <p className="mg-card-note">
            {portalText("operations", "this_is_taking_longer_than_it_should_wetalk_has_not_answer_7d31c66f")}</p>
          <div className="mg-card-actions">
            <Button variant="secondary" onClick={onRetry}>
              {portalText("operations", "retry_9f5cd8a2")}</Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function WebChatPage({
  detail,
  requestedVoiceAgentId,
}: {
  readonly detail: WebChatDetailView;
  readonly requestedVoiceAgentId: string | null;
}): ReactNode {
  usePortalTranslation("operations");
  const capability = useMemberCan();
  const canEdit = capability.can("web_chat_widget.manage");
  const refusedBecause =
    capability.role === null ? portalText("operations", "reading_what_your_role_can_change_bfc04284") : capability.whyNot("web_chat_widget.manage");
  const medium = detail.medium;
  const answerable = medium.answerableVoiceAgent;
  const requestedIsAnswerable =
    requestedVoiceAgentId !== null && answerable.some((one) => one.voiceAgentId === requestedVoiceAgentId);
  const [chosen, setChosen] = useState(
    requestedIsAnswerable && requestedVoiceAgentId !== null
      ? requestedVoiceAgentId
      : (answerable[0]?.voiceAgentId ?? ""),
  );
  const [focusRequest, setFocusRequest] = useState(0);
  const chosenIsAnswerable = answerable.some((one) => one.voiceAgentId === chosen);
  const shownChoice = chosenIsAnswerable ? chosen : (answerable[0]?.voiceAgentId ?? "");

  const grouped = (value: number): string => new Intl.NumberFormat((portalLocale() === "el" ? "el-GR" : "en-IE")).format(value);

  return (
    <section className="c-page mg-detail-page">
      <BackLink />

      <div className="mg-head">
        <span className="mg-head-mark">
          <Icon glyph={MEDIUM_GLYPH.web_chat} size="3xl" />
        </span>
        <div className="mg-head-body">
          <div className="mg-head-title">
            <h1 className="mg-detail-title">{medium.name}</h1>
            <Tag tone={MEDIUM_STATE_TONE[medium.state]}>{MEDIUM_STATE_LABEL[medium.state]}</Tag>
          </div>
          <p className="mg-blurb">{medium.blurb}</p>
        </div>
      </div>

      {medium.availability === "waitlisted" ? (
        <div className="c-panel mg-notice">
          <p className="mg-problem-title">{portalText("operations", "not_open_yet_6b3e8d60")}</p>
          <p className="c-empty-dek">{medium.waitlistReason ?? portalText("operations", "web_chat_needs_work_on_our_side_before_it_opens_c81b29a8")}</p>
        </div>
      ) : null}

      {capability.role !== null && !canEdit ? (
        <p className="c-note mg-readonly" role="note">
          {portalText("operations", "you_can_see_the_website_chat_here_but_not_change_it_2ebb4a61")}{refusedBecause} {portalText("operations", "ask_one_of_them_to_connect_it_change_its_sites_or_turn_it_b8d92109")}</p>
      ) : null}

      {detail.widget.length === 0 ? null : (
        <div className="mg-stats">
          <div className="mg-stat">
            <span className="mg-stat-value">{grouped(medium.conversationLast30Day)}</span>
            <span className="mg-stat-label">{portalText("operations", "conversations_last_30_days_4dc65e16")}</span>
          </div>
          <div className="mg-stat">
            <span className="mg-stat-value">{talkTimeText(medium.billedSecondLast30Day)}</span>
            <span className="mg-stat-label">{portalText("operations", "minutes_billed_last_30_days_b27b2e8a")}</span>
          </div>
          <div className="mg-stat">
            <span className="mg-stat-value">{grouped(medium.liveConversationCount)}</span>
            <span className="mg-stat-label">{portalText("operations", "happening_right_now_5f56731a")}</span>
          </div>
        </div>
      )}

      <div className="mg-widget-stack">
        {detail.widget.length === 0 ? (
          <p className="mg-empty">
            {portalText("operations", "no_website_chat_is_connected_yet_connect_one_below_choose_89a2c4d5")}</p>
        ) : (
          detail.widget.map((widget) => (
            <WidgetCard
              key={widget.channelBindingId}
              widget={widget}
              install={detail.install}
              canEdit={canEdit}
              refusedBecause={refusedBecause}
              onReconnect={(voiceAgentId) => {
                setChosen(voiceAgentId);
                setFocusRequest((count) => count + 1);
              }}
            />
          ))
        )}

        <ConnectWidgetPanel
          medium={medium}
          canEdit={canEdit}
          refusedBecause={refusedBecause}
          chosen={shownChoice}
          onChoose={setChosen}
          unanswerableRequested={requestedVoiceAgentId !== null && !requestedIsAnswerable}
          focusRequest={focusRequest}
        />
      </div>
    </section>
  );
}
